import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { useEffect, useMemo, useRef, useState } from "react";
import { TFeaturedComponent } from "src/Featuresmap";
import { GD } from "src/GD";
import { IPagination, Pagination } from "../conponents/Pagination";
import { Table } from "@ui/table/Table";
import { Error } from "@ui/error/Error";
import { cents, formatAmount } from "src/tools/tools";

interface IReportTransaction {
	id: number;
	yyyymm: number;
	yyyymmddhh: number;
	status: string;
	created_at: string;
	updated_at: string;
	fail_reason: string | null;
	project_id: number;
	payment_id: string;
	processing_id: string;
	mid: number;
	webhook_status: string;
	webhook_at: string | null;
	total_channels: number;
	amount: number;
	currency: string;
	rolling_reserve_amount: string | number | null;
	fees_amount: string | number | null;
	fee_conditions: string | null;
	terminal_id: number;
	channel_id: number;
	channel_alias: string | null;
	country: string;
	ip: string;
	email: string;
	settlement_status: string;
	institution: string;
	instrument: string;
	instrument_type: string;
	instrument_data: string;
	instrument_name: string;
	interchange_fee: string | number | null;
	turnover_fee: string | number | null;
	scheme_fee: string | number | null;
	processing_mid: number;
	sandbox: number;
	ref_id: string | number | null;
	type: string;
	settlement_date: string | null;
	reserve_release_date: string | null;
	rolling_reserver_percent: number | null;
	net: string | number;
	_is_release?: boolean;
}

type ReportSessionsProps = {
	id: number;
	reportDate?: string;
	onCsvStateChange?: (state: { busy: boolean; canDownload: boolean; download: () => void }) => void;
};

const requestPerPage = 10000;
const minLocalPerPage = 10;
const estimatedTableHeaderHeight = 32;
const estimatedTableRowHeight = 29;
const tableBottomSafety = 8;

const getReportTransactions = (data: any): IReportTransaction[] => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.rows)) return data.rows;
	if (Array.isArray(data?.items)) return data.items;
	return [];
};

const getNumericAmount = (value: string | number | null | undefined): number => {
	const amount = Number(value ?? 0);
	return Number.isFinite(amount) ? amount : 0;
};

const formatMoneyCell = (value: string | number | null | undefined) => formatAmount(getNumericAmount(value));

const isReleaseTransaction = (row: IReportTransaction) => row._is_release === true;

const reportTransactionCsvHeaderPriority = [
	"id",
	"created_at",
	"settlement_date",
	"type",
	"status",
	"settlement_status",
	"amount",
	"currency",
	"fees_amount",
	"rolling_reserve_amount",
	"net",
	"channel_id",
	"channel_alias",
	"mid",
	"country",
	"email",
	"payment_id",
	"terminal_id",
	"fail_reason",
];

const getReportTransactionCsvHeaders = (rows: Record<string, unknown>[]) => {
	const availableHeaders = Array.from(new Set(rows.flatMap(row => Object.keys(row))))
		.filter(header => header !== "processing_mid" && header !== "processing_id" && header !== "current_channel_index");
	const prioritizedHeaders = reportTransactionCsvHeaderPriority.filter(header => availableHeaders.includes(header));
	const extraHeaders = availableHeaders.filter(header => !reportTransactionCsvHeaderPriority.includes(header)).sort();

	return [...prioritizedHeaders, ...extraHeaders];
};

const getReportTransactionCsvCellValue = (header: string, value: unknown) => {
	if (header === "amount" && typeof value === "number") return (value / 100).toFixed(2);
	if (value == null) return "";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

const escapeCsvCell = (value: string) => {
	return value.includes(",") || value.includes('"') || value.includes("\n") ? '"' + value.replace(/"/g, '""') + '"' : value;
};

const sanitizeFileNamePart = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");

const downloadCsvBlob = (csv: string, fileName: string) => {
	const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	a.click();
	URL.revokeObjectURL(url);
};

const getPreviewTransaction = (row: IReportTransaction) => {
	if (!isReleaseTransaction(row)) return row;

	return {
		id: row.id,
		// ...row,
		// amount: Math.abs(getNumericAmount(row.net)) * 100,
		// type: "release",
		// status: "completed",
		// settlement_status: "completed",
		// settlement_date: reportDate ?? row.settlement_date,
		// fees_amount: 0,
		// rolling_reserve_amount: 0,
		// processing_mid: row.processing_mid ?? row.mid,
	};
};

export const ReportSessions: TFeaturedComponent<ReportSessionsProps> = ({ id, reportDate, onCsvStateChange }) => {
	 useLocale();
	const [searchObject, setSearchObject] = useState<{ search: string }>({ search: "" });
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [localPerPage, setLocalPerPage] = useState<number>(minLocalPerPage);
	const [downloadBusy, setDownloadBusy] = useState(false);
	const tableBoxRef = useRef<HTMLDivElement>(null);

	const getRequestData = (search = searchObject.search, overrides: Record<string, unknown> = {}) => {
		return {
			search: search ? search : undefined,
			pagination: {
				page: 0,
				perPage: requestPerPage,
			},
			report_id: id,
			...overrides,
		};
	};

	//!TODO: duplicate code with Sessions form, need to be refactored
	const [response, reload, _reset, _busy] = GD.REQ_API.useRequest(null, {
		method: "reports/trx/get",
		data: getRequestData(),
	});

	const cellSx = {
		fontFamily: "monospace",
		fontSize: "14px",
		whiteSpace: "nowrap" as const,
	};

	const handleSearch = () => {
		setCurrentPage(0);
		reload({
			method: "reports/trx/get",
			data: getRequestData(),
		});
		setSearchObject({ search: searchObject.search });
	};

	const resetSearch = () => {
		setSearchObject({ search: "" });
		setCurrentPage(0);
		reload({
			method: "reports/trx/get",
			data: getRequestData(""),
		});
	};

	const handleRefresh = () => {
		reload({
			method: "reports/trx/get",
			data: getRequestData(),
		});
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const items = getReportTransactions(response?.data);
	const initialLoading = _busy && !response;
	const refreshing = _busy && !!response;
	const totalPages = Math.max(1, Math.ceil(items.length / localPerPage));
	const pagedItems = useMemo(() => items.slice(currentPage * localPerPage, (currentPage + 1) * localPerPage), [items, currentPage, localPerPage]);
	const paginationData: IPagination | undefined = items.length
		? {
				total: items.length,
				page: Math.min(currentPage, totalPages - 1),
				perPage: localPerPage,
				totalPages,
			}
		: undefined;

	const handleDownloadCsv = async () => {
		if (downloadBusy) return;

		setDownloadBusy(true);
		try {
			const res = await GD.REQ_API.request({
				method: "reports/trx/get",
				data: getRequestData(searchObject.search, { download: true }),
			});
			const rows = getReportTransactions(res?.data) as unknown as Record<string, unknown>[];

			if (!rows || rows.length === 0) return;

			const headers = getReportTransactionCsvHeaders(rows);
			const csvRows = rows.map(row => headers.map(header => escapeCsvCell(getReportTransactionCsvCellValue(header, row[header]))).join(","));
			const csv = [headers.join(","), ...csvRows].join("\n");
			const fileNameParts = ["report", sanitizeFileNamePart(String(id)), "transactions"];
			if (searchObject.search) fileNameParts.push(sanitizeFileNamePart(searchObject.search));
			downloadCsvBlob(csv, fileNameParts.join("_") + ".csv");
		} finally {
			setDownloadBusy(false);
		}
	};

	useEffect(() => {
		onCsvStateChange?.({
			busy: downloadBusy,
			canDownload: items.length > 0 && !_busy,
			download: handleDownloadCsv,
		});
	}, [downloadBusy, items.length, _busy, id, searchObject.search, onCsvStateChange]);

	useEffect(() => {
		const measureLocalPageSize = () => {
			const tableBox = tableBoxRef.current;
			if (!tableBox) return;

			const availableHeight = tableBox.clientHeight;
			if (availableHeight <= 0) return;

			const nextPerPage = Math.max(minLocalPerPage, Math.floor((availableHeight - estimatedTableHeaderHeight - tableBottomSafety) / estimatedTableRowHeight));
			setLocalPerPage(current => (current === nextPerPage ? current : nextPerPage));
		};

		measureLocalPageSize();
		window.addEventListener("resize", measureLocalPageSize);

		const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measureLocalPageSize) : null;
		if (tableBoxRef.current && resizeObserver) resizeObserver.observe(tableBoxRef.current);

		return () => {
			window.removeEventListener("resize", measureLocalPageSize);
			resizeObserver?.disconnect();
		};
	}, []);

	useEffect(() => {
		if (currentPage > totalPages - 1) setCurrentPage(Math.max(0, totalPages - 1));
	}, [currentPage, totalPages]);

	useEffect(() => {
		const tableBox = tableBoxRef.current;
		if (!tableBox || pagedItems.length === 0) return;

		const tableContent = tableBox.firstElementChild instanceof HTMLElement ? tableBox.firstElementChild : null;
		const contentHeight = Math.max(tableContent?.getBoundingClientRect().height ?? 0, tableBox.scrollHeight);
		const overflow = contentHeight - tableBox.clientHeight;
		const actualRowHeight = Math.max(1, (contentHeight - estimatedTableHeaderHeight) / pagedItems.length);

		if (overflow > 1 && localPerPage > minLocalPerPage) {
			const rowsToRemove = Math.max(1, Math.ceil(overflow / actualRowHeight));
			setLocalPerPage(current => Math.max(minLocalPerPage, current - rowsToRemove));
			return;
		}

	}, [currentPage, items.length, pagedItems.length, localPerPage]);

	return (
		<VBox gap="10px" sx={{ flex: "1 1 0", minHeight: 0, width: "100%", overflow: "hidden" }}>
			<ProgressBar busy={_busy || downloadBusy} />
			<HBox gap="8px" sx={{ alignItems: "center", flexShrink: 0, width: "100%" }}>
				<Input
					sx={{
						flexGrow: "1",
						minWidth: "220px",
					}}
					size="small"
					type="search"
					name="search"
					placeholder={msg("search_7f553822")}
					data={searchObject}
				/>

				<Button onClick={() => resetSearch()} variant="secondarySmall" title={msg("reset")} />
				<Button onClick={() => handleSearch()} title={msg("search")} variant="primarySmall" />
			</HBox>

			{response?.error && <Error error={response.error} />}
			{refreshing && (
				<Typo sx={{ color: "#64748B", fontSize: "12px", flexShrink: 0 }}>{msg("refreshingTransactions")}</Typo>
			)}

			<Box ref={tableBoxRef} sx={{ display: "flex", flex: "1 1 0", minHeight: 0, width: "100%", overflowX: "auto", overflowY: "auto" }}>
				{initialLoading && (
					<VBox sx={{ width: "100%", height: "100%", alignItems: "center", justifyContent: "center" }}>
						<Typo sx={{ color: "#64748B", fontSize: "13px" }}>{msg("loadingTransactions")}</Typo>
					</VBox>
				)}
				{!initialLoading && response && response.data && (
					<Table<IReportTransaction>
						headPadding="5px 10px"
						cellPadding="5px 10px"
						sx={{ width: "100%", fontFamily: "monospace" }}
						onRowClick={(row, _cell) => {
							GD.S_TRANSACTION_PREVIEW.invoke(getPreviewTransaction(row) as any);
						}}
						headerFactory={val => {
							if (typeof val === "string") {
								return {
									content: (
										<Typo
											variant="medium"
											sx={{
												fontWeight: 700,
												fontFamily: "monospace",
												fontSize: "11px",
												textTransform: "uppercase",
												letterSpacing: "0.03em",
											}}>
											{val.toUpperCase()}
										</Typo>
									),
								};
							}
							return { content: null };
						}}
						headers={[
							{ value: "settlement date", gridColumn: "max-content" },
							{ value: "type", gridColumn: "max-content" },
							{ value: "status", gridColumn: "max-content" },
							{ value: "currency", gridColumn: "max-content" },
							{ value: "processing mid", gridColumn: "max-content" },
							{ value: "channel", gridColumn: "max-content" },
							{ value: "amount", gridColumn: "auto", sx: { textAlign: "right" } },
							{ value: "fee", gridColumn: "auto", sx: { textAlign: "right" } },
							{ value: "rolling", gridColumn: "auto", sx: { textAlign: "right" } },
							{ value: "net", gridColumn: "auto", sx: { textAlign: "right" } },
						]}
						//!TODO:
						order={[
							"settlement date" as any,
							"type",
							"status" as any,
							"currency" as any,
							"processing mid" as any,
							"channel" as any,
							"amount",
							"fee" as any,
							"rolling" as any,
							"net" as any,
						]}
						gridTemplateColumns="max-content max-content max-content max-content max-content max-content auto auto auto auto"
						cellFactory={(row, _cell, _index, _key, name) => {
							if (name === "type") {
								if (isReleaseTransaction(row)) {
									return {
										content: <Typo sx={{ ...cellSx, color: "#2563EB", fontWeight: 600 }}>{msg("release")}</Typo>,
									};
								}

								const type = row.type?.toLowerCase();
								const isRefund = type === "refund";
								const isDmsOrTransfer = type === "dms" || type === "transfer";
								const textColor = isRefund ? "#DC2626" : isDmsOrTransfer ? "#16A34A" : "#64748B";
								return {
									content: <Typo sx={{ ...cellSx, color: textColor, fontWeight: 600 }}>{row.type?.toUpperCase()}</Typo>,
								};
							}
							if (name === "status") {
								if (isReleaseTransaction(row)) {
									return {
										content: <Typo sx={{ ...cellSx, color: "#16A34A", fontWeight: 600 }}>{msg("completed_c48179f5")}</Typo>,
									};
								}

								const status = row.settlement_status?.toLowerCase();
								const statusColor = status === "completed" ? "#16A34A" : status === "failed" ? "#DC2626" : "#64748B";
								const failTitle = status === "failed" && row.fail_reason ? row.fail_reason : undefined;
								return {
									content: (
										<span title={failTitle} style={{ cursor: failTitle ? "help" : undefined }}>
											<Typo sx={{ ...cellSx, color: statusColor, fontWeight: 600 }}>{row.settlement_status?.toUpperCase() ?? "—"}</Typo>
										</span>
									),
								};
							}
							if (name === "amount") {
								if (isReleaseTransaction(row)) {
									return {
										content: (
											<Typo sx={{ ...cellSx, fontWeight: 600, textAlign: "end", display: "block" }}>
												{formatMoneyCell(Math.abs(getNumericAmount(row.net)))}
											</Typo>
										),
									};
								}

								const type = row.type?.toLowerCase();
								// refunds, chargebacks and OCT payouts are all money leaving the merchant —
								// show the amount red with a minus sign, same treatment for all.
								const isReversal = type === "refund" || type === "chargeback" || type === "oct";
								const amountColor = isReversal ? "#DC2626" : undefined;
								const prefix = isReversal ? "-" : "";
								return {
									content: (
										<Typo sx={{ ...cellSx, color: amountColor, fontWeight: 600, textAlign: "end", display: "block" }}>
											{prefix + cents(row.amount)}
										</Typo>
									),
								};
							}
							if (name === "currency") {
								return {
									content: <Typo sx={cellSx}>{row.currency ?? ""}</Typo>,
								};
							}
							if (name === "fee") {
								return {
									content: <Typo sx={{ ...cellSx, textAlign: "end", display: "block" }}>{formatMoneyCell(isReleaseTransaction(row) ? 0 : row.fees_amount)}</Typo>,
								};
							}
							if (name === "rolling") {
								return {
									content: (
										<Typo sx={{ ...cellSx, textAlign: "end", display: "block" }}>
											{formatMoneyCell(isReleaseTransaction(row) ? 0 : row.rolling_reserve_amount)}
										</Typo>
									),
								};
							}
							if (name === "net") {
								return {
									content: <Typo sx={{ ...cellSx, fontWeight: 600, textAlign: "end", display: "block" }}>{formatMoneyCell(row.net)}</Typo>,
								};
							}
							if (name === "settlement date") {
								const settlementDate = isReleaseTransaction(row) ? reportDate : row.settlement_date;
								const val = formatDisplayDate(settlementDate);
								return {
									content: <Typo sx={cellSx}><DateTimeTooltip value={settlementDate}>{val}</DateTimeTooltip></Typo>,
								};
							}
							if (name === "processing mid") {
								return {
									content: <Typo sx={cellSx}>{row.processing_mid ?? ''}</Typo>,
								};
							}
							if (name === "channel") {
								return {
									content: <Typo sx={cellSx}>{row.channel_alias || row.channel_id}</Typo>,
								};
							}
							return { content: null };
						}}
						data={pagedItems}
					/>
				)}
			</Box>

			<HBox gap="8px" sx={{ alignItems: "center", minHeight: "30px", flexShrink: 0 }}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} />
				<Spacer grow />
				<Button onClick={() => handleRefresh()} title={refreshing ? msg("refreshing") : msg("refresh")} variant="primarySmall" disabled={_busy} />
			</HBox>
		</VBox>
	);
};

ReportSessions.feature = "report_sessions";
